/*
 * Hotel instant confirmation - website display rules (QA doc: docs features/hotel-instant-confirmation/qa.md).
 * Enforces R-1 (chip + smallest rooms left), R-7 (calendar nights, book-by), R-8 (lost reason texts), R-15 / R-17 (band colour or
 * the site's default). The decision itself is hotels-api's (architecture A); nothing here decides whether a price is instant.
 */
import { InstantColourBand, InstantLostReason, PackageHotel } from '@appTypes/api.types';

/**
 * The palette keys a colour band may name (the operator picks them in Sales ▸ Destinations ▸ Instant confirmation ▸ Colours).
 * Each has its classes in styles.scss (`instant-band-<key>`, `instant-day-<key>`); a key the site has no style for is shown
 * with the site's default colour instead of an unstyled class (R-17).
 */
export const INSTANT_PALETTE = ['green', 'yellow', 'orange', 'red', 'blue'] as const;
export type InstantPaletteKey = (typeof INSTANT_PALETTE)[number];

/** Code-style translation keys (ICU plurals: they are also in en-US.json, C21). */
export const INSTANT_ROOMS_LEFT_KEY = 'INSTANT_ROOMS_LEFT';
export const INSTANT_BAND_ROOMS_KEY = 'INSTANT_BAND_ROOMS';
export const INSTANT_BAND_RANGE_KEY = 'INSTANT_BAND_RANGE';
export const INSTANT_BAND_OR_MORE_KEY = 'INSTANT_BAND_OR_MORE';

export const isInstantPaletteKey = (colour?: string | null): colour is InstantPaletteKey =>
  !!colour && (INSTANT_PALETTE as readonly string[]).includes(colour);

/** true when hotels-api tagged the price (R-1); a price without the flag is never shown as instant (R-2). */
export const isInstantHotel = (hotel?: PackageHotel | null): boolean => hotel?.instantConfirmation === true;

/** The id the chip, the count and the calendar are addressed by (C16): the hotel price id, else the package id. */
export const instantPriceId = (hotel?: PackageHotel | null, fallbackId?: string | null): string =>
  hotel?.hotelPriceId ?? fallbackId ?? '';

/** R-15 / R-17: `instant-band-<colour>` for a palette colour, '' (the site's default) otherwise. */
export const instantBandClass = (colour?: string | null): string =>
  isInstantPaletteKey(colour) ? `instant-band-${colour}` : '';

/** R-15: the band containing `rooms` - the calendar colours each night by it. */
export const instantBandFor = (rooms: number, bands?: InstantColourBand[] | null): InstantColourBand | undefined =>
  bands?.find((b) => rooms >= b.minRooms && (b.maxRooms === null || b.maxRooms === undefined || rooms <= b.maxRooms));

/** A translation key with its ICU parameters. */
export interface InstantText {
  key: string;
  params: Record<string, number | string>;
}

/** The legend text of one band: "1 room", "2-4 rooms", "5 or more rooms" (RU: ru-RU.json, plural forms by the count). */
export const instantBandLabel = (band: InstantColourBand): InstantText => {
  if (band.maxRooms === null || band.maxRooms === undefined)
    return { key: INSTANT_BAND_OR_MORE_KEY, params: { min: band.minRooms } };
  if (band.minRooms === band.maxRooms) return { key: INSTANT_BAND_ROOMS_KEY, params: { value: band.minRooms } };
  return { key: INSTANT_BAND_RANGE_KEY, params: { min: band.minRooms, max: band.maxRooms } };
};

/** The bands the legend lists: those with a palette colour, from the fewest rooms up. */
export const instantLegendBands = (bands?: InstantColourBand[] | null): InstantColourBand[] =>
  (bands ?? []).filter((b) => isInstantPaletteKey(b.colour)).sort((a, b) => a.minRooms - b.minRooms);

/**
 * R-8 / qa.md Error codes: the text of INSTANT_CONFIRMATION_LOST per reason (the key is the English text, ru-RU.json has the
 * Russian). A reason this site does not know falls back to the backend's message, else to the general text.
 */
export const INSTANT_LOST_REASON_TEXT: Record<InstantLostReason, string> = {
  NO_ROOMS_LEFT:
    "Instant confirmation is no longer available - the last rooms were booked. The booking will wait for the supplier's answer",
  RELEASE_PASSED:
    "Instant confirmation is no longer available - the booking deadline has passed. The booking will wait for the supplier's answer",
  STOP_SALE:
    "Instant confirmation is no longer available - the hotel stopped sales. The booking will wait for the supplier's answer",
  SWITCHED_OFF:
    "Instant confirmation is no longer available - instant confirmation was switched off. The booking will wait for the supplier's answer",
  UNAVAILABLE:
    "Instant confirmation information is not available right now - the booking will wait for the supplier's answer"
};
export const INSTANT_LOST_GENERAL_TEXT =
  "Instant confirmation is no longer available. The booking will wait for the supplier's answer";

/** R-10: the note shown once the agent continued on request. */
export const INSTANT_ON_REQUEST_TEXT = "The booking will wait for the supplier's answer.";

export const instantLostText = (reason?: string | null, backendMessage?: string | null): string => {
  const known = reason ? INSTANT_LOST_REASON_TEXT[reason as InstantLostReason] : undefined;
  if (known) return known;
  if (backendMessage) return backendMessage; // an empty text is no text
  return INSTANT_LOST_GENERAL_TEXT;
};
